import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { FavoritesDataService, FavoritesColumnsService } from '../../services';
import { DialogFavoriteFormComponent } from './favorite-form/favorite-form.component';
import { DialogFavoriteDetailsComponent } from './favorite-details/favorite-details.component';
import { Favorite, FavoritesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-favorites',
  templateUrl: './favorites.component.html',
  styleUrls: ['./favorites.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class FavoritesComponent implements OnInit, OnDestroy {

  private favoriteAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private favoriteEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private favoriteDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public favoriteToDelete: Favorite = {};
  public favorites: Favorite[] = [];
  public favorite: Favorite = {};
  public selectedFavorites: Favorite[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private favoritesDataService: FavoritesDataService,
    private favoritesColumnsService: FavoritesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('FavoritesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Favorites - Management');
    this.primengConfig.ripple = true;
    this.getFavorites();
  }

  ngOnDestroy(): void {
    console.log('FavoritesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getFavorites(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.favoritesDataService.getFavorites(this.parameters).subscribe((response: FavoritesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.favorites = response.results;
        } else {
          this.favorites = this.favorites.concat(response.results);
        }
        if (response.inlineCount > this.favorites.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getFavorites();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FAVORITESNOTLOADED') });
    });
  }

  importFavorites(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.favoritesDataService.addFavorites(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FAVORITESCREATED') });
                if (proceed === j) {
                  this.getFavorites();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FAVORITESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FAVORITESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.favoritesDataService.updateFavorites(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FAVORITESUPDATED') });
                if (proceed === j) {
                  this.getFavorites();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FAVORITESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FAVORITESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.favoritesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('favorites.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedFavorites() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Favorites?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.favoritesDataService.deleteFavorites(this.selectedFavorites).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Favorites Deleted', life: 3000 });
            this.getFavorites();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Favorites not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Favorites not deleted' });
        });
        this.selectedFavorites = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.favorites, this.columns, 'Favorites');
  }

  exportExcel() {
    ToolsService.exportExcel(this.favorites, this.columns, 'Favorites');
  }

  exportCsv() {
    ToolsService.exportCsv(this.favorites, this.columns, 'Favorites');
  }

  refresh() {
    this.favoritesDataService.clearCache();
    this.getFavorites();
  }

  favoriteAdd(): void {
    if (!this.navigation.getParamValue('favorites.addInDialog', true)) {
      this.router.navigate(['/favorites/add']);
      return;
    }
    this.favoriteAddDialog = this.dialogService.open(DialogFavoriteFormComponent, {
      header: '', // this.translate.instant('NEWFAVORITE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        favorite: { id: null },
        users: this.favoritesColumnsService.users,
      },
    });

    this.favoriteAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Favorite added' });
        this.getFavorites();
      }
    });
  }

  favoriteEdit(favorite: Favorite): void {
    if (!this.navigation.getParamValue('favorites.editInDialog', true)) {
      this.router.navigate(['/favorites/edit/' + favorite.id]);
      return;
    }
    this.favoriteEditDialog = this.dialogService.open(DialogFavoriteFormComponent, {
      header: '', // this.translate.instant('EDITFAVORITE') + ' ' + favorite.entity,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        favorite: favorite,
        users: this.favoritesColumnsService.users,
      },
    });

    this.favoriteEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Favorite updated' });
        this.getFavorites();
      }
    });
  }

  favoriteDetails(favorite: Favorite): void {
    if (!this.navigation.getParamValue('favorites.detailsInDialog', true)) {
      this.router.navigate(['/favorites/details/' + favorite.id]);
      return;
    }
    this.favoriteDetailsDialog = this.dialogService.open(DialogFavoriteDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        favorite: favorite,
      }
    });
  }

  favoriteDelete(favorite: Favorite): void {
    this.favoriteToDelete = favorite;
    this.messageService.clear();
    this.messageService.add({
      key: 'favoriteDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + favorite.entity + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.favoritesDataService.deleteFavorite(this.favoriteToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Favorite deleted' });
      this.getFavorites();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Favorite not deleted' });
    });
  }

  onDeleteReject(): void {
    this.favoriteToDelete = {};
    this.messageService.clear('favoriteDelete');
  }

}
